import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  size,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

import { Color } from '@tmh-website/shared/colors';
import { usePortalContainer } from '@tmh-website/shared/hooks';

import * as Styled from './InfoTextButton.styles';
import { InfoTextButtonThemeColor, infoTextButtonThemes } from './InfoTextButton.theme';

/**
 * Distance between tooltip and button and tooltip and window edge.
 */
const OFFSET_VALUE = 6;

export interface InfoTextButtonProps {
  /**
   * Optional props for the "i" icon (tooltip toggler).
   *
   * @default {}
   */
  iconProps?: Partial<ButtonHTMLAttributes<HTMLButtonElement | HTMLDivElement>>;

  infoText: ReactNode;
  isSmall?: boolean;
  placement?: Placement;
  fallbackPlacements?: Placement[];
  themeColor?: InfoTextButtonThemeColor;
  customTooltipThemeColor?: InfoTextButtonThemeColor;
  /**
   * Adjust the max-width of the popup on desktop
   */
  customDesktopMaxWidth?: number;
  padding?: number;
  as?: 'button' | 'div';
}

const InfoTextButton = ({
  iconProps = {},
  infoText,
  isSmall,
  placement = 'top' as Placement,
  fallbackPlacements,
  customDesktopMaxWidth,
  themeColor = Color.Neutral_0_White as InfoTextButtonThemeColor,
  customTooltipThemeColor,
  padding = 6,
  as = 'button',
  ...props
}: InfoTextButtonProps) => {
  const id = useId();
  const container = usePortalContainer({ id });
  const { t } = useTranslation();
  const [tooltipIsOpen, setTooltipIsOpen] = useState(false);
  const tooltipThemeColor = customTooltipThemeColor ?? infoTextButtonThemes[themeColor].tooltipThemeColor;

  const { context, x, y, reference, floating, strategy } = useFloating({
    open: tooltipIsOpen,
    onOpenChange: setTooltipIsOpen,
    placement,
    middleware: [
      offset(OFFSET_VALUE),
      shift({ padding }),
      flip({ fallbackPlacements: fallbackPlacements }),
      size({
        apply({ availableWidth, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            maxWidth: `${availableWidth - OFFSET_VALUE * 2}px`,
            maxHeight: `${availableHeight - OFFSET_VALUE}px`,
          });
        },
      }),
    ],
    strategy: 'fixed',
    whileElementsMounted(...args) {
      return autoUpdate(...args, { animationFrame: true });
    },
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useHover(context, {
      delay: {
        close: 350,
      },
    }),
    useFocus(context),
    useRole(context, { role: 'tooltip' }),
  ]);

  if (container === null) return;

  return (
    <>
      <Styled.BtnInfoText
        $isSmall={!!isSmall}
        $themeColor={themeColor}
        aria-label={t('global:showInfoText') as string}
        as={as}
        type='button'
        {...getReferenceProps({ ref: reference })}
        {...props}
        {...iconProps}>
        <Styled.IIcon $style='uiTextSmall' as='span'>
          i
        </Styled.IIcon>
      </Styled.BtnInfoText>

      {createPortal(
        tooltipIsOpen && (
          <Styled.InfoTooltip
            $customDesktopMaxWidth={customDesktopMaxWidth}
            $themeColor={tooltipThemeColor}
            {...getFloatingProps({
              ref: floating,
              style: {
                position: strategy,
                left: x ?? '',
                top: y ?? '',
              },
            })}>
            {infoText}
          </Styled.InfoTooltip>
        ),
        container
      )}
    </>
  );
};

export default InfoTextButton;
